import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { libraryCompaniesQueryKeys } from '@/hooks/library/types'
import { removeDeletedEntitiesFromRelationships } from '@/hooks/library/useEntityRelationships'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'

interface UseDeleteCompaniesArgs {
  orgId: UUID | ''
}

interface DeleteCompaniesReq {
  ids: UUID[]
}

const deleteCompanies = async (
  req: DeleteCompaniesReq,
  orgId: UUID | '',
  accessToken: string,
): Promise<void> => {
  if (req.ids.length === 0) {
    return
  }
  if (!orgId) {
    throw new Error('No org id provided.')
  }
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/bulk`,
    {
      method: 'DELETE',
      body: JSON.stringify(req.ids),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

export const useDeleteCompanies = ({ orgId }: UseDeleteCompaniesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    DeleteCompaniesReq,
    PaginatedResponse<EntityCompany> | undefined
  >({
    mutationFn: async (req) => {
      await deleteCompanies(req, orgId, accessToken)
    },
    onMutate: async (req) => {
      const key = libraryCompaniesQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })

      const previousData =
        queryClient.getQueryData<PaginatedResponse<EntityCompany>>(key)

      if (previousData) {
        const remaining = previousData.data.filter(
          (company) => !req.ids.includes(company.id),
        )
        queryClient.setQueryData<PaginatedResponse<EntityCompany>>(key, {
          ...previousData,
          data: remaining,
          count: remaining.length,
        })
      }

      return previousData
    },
    onError: (err, files, context) => {
      const key = libraryCompaniesQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data, req) => {
      const key = libraryCompaniesQueryKeys.organization({ orgId })

      // Remove deleted entities from entity relationship queries
      removeDeletedEntitiesFromRelationships(queryClient, req.ids)

      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
